/**
 * TrainingType Edit Page
 * Page for editing existing TrainingType
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PageShell } from '../../components';
import { trainingTypeService } from '../../services/trainingTypeService';
import { TrainingTypeData, TrainingTypeFormData, TrainingTypeStatus } from '../../types/trainingType';
import { TrainingTypeForm } from './components/TrainingTypeForm';

function toFormData(data: TrainingTypeData): TrainingTypeFormData {
  return {
    name: data.name,
    description: data.description ?? undefined,
    legalBasis: data.legalBasis ?? undefined,
    workload: data.workload ?? null,
    periodicityMonths: data.periodicityMonths ?? null,
    esocialCode: data.esocialCode ?? undefined,
    certificateText: data.certificateText ?? undefined,
    status: data.status as TrainingTypeStatus,
  };
}

const BREADCRUMBS = [
  { label: 'Início', path: '/' },
  { label: 'Tipos de Treinamento', path: '/training-types' },
  { label: 'Editar Tipo' },
];

export const TrainingTypeEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [trainingType, setTrainingType] = useState<TrainingTypeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTrainingType = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await trainingTypeService.getById(Number(id));
        setTrainingType(data);
      } catch (err: unknown) {
        const error = err as { message?: string };
        setError(error.message ?? 'Erro ao carregar tipo de treinamento');
      } finally {
        setLoading(false);
      }
    };

    loadTrainingType();
  }, [id]);

  const handleSubmit = async (data: TrainingTypeFormData) => {
    if (!id) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await trainingTypeService.update(Number(id), data);
      navigate('/training-types');
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao atualizar tipo de treinamento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/training-types');
  };

  if (loading) {
    return (
      <PageShell breadcrumbs={BREADCRUMBS} title="Editar Tipo de Treinamento">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!trainingType) {
    return (
      <PageShell breadcrumbs={BREADCRUMBS} title="Editar Tipo de Treinamento">
        <Alert severity="error">Tipo de treinamento não encontrado</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={BREADCRUMBS}
      title="Editar Tipo de Treinamento"
      subtitle={trainingType.name}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TrainingTypeForm
            initialData={toFormData(trainingType)}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default TrainingTypeEditPage;
